<!--
 * @Author: AI Assistant
 * @Date: 2025-12-30
 * @Description: 创意描述与风格标签组合组件
 * @FilePath: /weryai-web/components/Tool/PromptWithTags.vue
-->
<template>
    <div class="mt-5">
        <!-- 整合容器 -->
        <div
            class="relative w-full bg-music-item-default rounded-lg border border-transparent p-[20px] transition-colors duration-200"
            :class="{
                'border-active-border': isFocused,
                'hover:border-white/30': !isFocused,
            }"
            :style="{
                borderColor: isFocused ? '#ffffff4d' : '',
            }"
        >
            <!-- 标题 -->
            <label for="prompt-textarea" class="block text-[#A0A0A8] text-xs mb-2.5">
                {{ t('music.creativeDescription') }}
            </label>

            <!-- 已选标签区域 -->
            <div
                v-if="selectedTagsList.length > 0"
                class="flex flex-wrap gap-2 mb-2"
            >
                <div
                    v-for="item in selectedTagsList"
                    :key="item.key"
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#2A2A2E] text-white rounded text-xs leading-none"
                >
                    <span>{{ item.value }}</span>
                    <button
                        @click.stop="removeTagByKey(item.key)"
                        class="hover:bg-white/10 rounded-full w-3.5 h-3.5 flex items-center justify-center"
                    >
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                            <path
                                d="M1 1L7 7M7 1L1 7"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linecap="round"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            <!-- 文字输入区域 -->
            <textarea
                id="prompt-textarea"
                ref="inputRef"
                v-model="inputText"
                :placeholder="computedPlaceholder"
                @focus="isFocused = true"
                @blur="isFocused = false"
                @input="updatePrompt"
                class="w-full min-h-[80px] bg-transparent text-white text-sm resize-none outline-none border-none appearance-none placeholder:text-[#8A8A90]"
                :aria-label="t('music.creativeDescription')"
            ></textarea>

            <!-- 字符计数 -->
            <div class="flex justify-end">
                <span
                    class="text-xs transition-colors duration-200"
                    :class="{
                        'text-yellow-400': isNearLimit && !isAtLimit,
                        'text-red-400': isAtLimit,
                        'text-[#A0A0A8]': !isNearLimit,
                    }"
                >
                    {{ characterCount }}/{{ maxLength }}
                </span>
            </div>

            <!-- 风格标签区域 -->
            <div class="pt-3">
                <!-- Tab 标题 -->
                <div class="flex gap-5 mb-3 relative">
                    <!-- 底部边框线 -->
                    <div
                        class="absolute bottom-0 left-0 w-full h-px bg-white/5"
                    ></div>

                    <!-- 曲风 Tab -->
                    <button
                        @click="currentCategory = 'genre'"
                        class="text-xs pb-3 relative font-semibold transition-colors duration-200"
                        :class="
                            currentCategory === 'genre'
                                ? 'text-white'
                                : 'text-[#A0A0A8] hover:text-white/70'
                        "
                    >
                        {{ t('menus.musicCategories.genre') }}
                        <span
                            v-if="currentCategory === 'genre'"
                            class="absolute bottom-0 left-0 w-full h-0.5 bg-[#99F768] rounded"
                        ></span>
                    </button>

                    <!-- 情感 Tab -->
                    <button
                        @click="currentCategory = 'emotion'"
                        class="text-xs pb-3 relative font-semibold transition-colors duration-200"
                        :class="
                            currentCategory === 'emotion'
                                ? 'text-white'
                                : 'text-[#A0A0A8] hover:text-white/70'
                        "
                    >
                        {{ t('menus.musicCategories.emotion') }}
                        <span
                            v-if="currentCategory === 'emotion'"
                            class="absolute bottom-0 left-0 w-full h-0.5 bg-[#99F768] rounded"
                        ></span>
                    </button>

                    <!-- 乐器 Tab -->
                    <button
                        @click="currentCategory = 'instrument'"
                        class="text-xs pb-3 relative font-semibold transition-colors duration-200"
                        :class="
                            currentCategory === 'instrument'
                                ? 'text-white'
                                : 'text-[#A0A0A8] hover:text-white/70'
                        "
                    >
                        {{ t('menus.musicCategories.instrument') }}
                        <span
                            v-if="currentCategory === 'instrument'"
                            class="absolute bottom-0 left-0 w-full h-0.5 bg-[#99F768] rounded"
                        ></span>
                    </button>
                </div>

                <!-- 标签按钮 -->
                <div class="flex flex-wrap gap-2 min-h-[60px]">
                    <button
                        v-for="tag in currentTags"
                        :key="tag.value"
                        @click="toggleTag(tag)"
                        class="px-3 py-1.5 text-xs rounded-md transition-all duration-200 border"
                        :class="[
                            isTagSelected(tag.value)
                                ? 'bg-white/5 text-white border-white/50'
                                : 'bg-white/5 text-[#A0A0A8] border-transparent hover:bg-white/10',
                        ]"
                    >
                        {{ tag.label }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from '~/composables/useI18n'

const { t } = useI18n()

// Props
const props = withDefaults(
    defineProps<{
        placeholder?: string
        maxLength?: number
    }>(),
    {
        placeholder: '',
        maxLength: 5000,
    }
)

// 计算 placeholder，支持国际化
const computedPlaceholder = computed(
    () => props.placeholder || t('music.creativeDescriptionPlaceholder')
)

// Models
const prompt = defineModel<string>('prompt', {
    default: '',
})

// 风格 Map 类型（key: 枚举值, value: 中文名称）
const styles = defineModel<Record<string, string>>('styles', {
    default: () => ({}),
})

// 用户输入的纯文本（暴露给父组件用于验证）
const inputText = defineModel<string>('inputText', {
    default: '',
})

// Emits
const emit = defineEmits<{
    (e: 'tag-change', styles: Record<string, string>): void
}>()

// UI 状态
const isFocused = ref(false)
const inputRef = ref<HTMLTextAreaElement>()
const currentCategory = ref<'genre' | 'emotion' | 'instrument'>('genre')

// 风格标签接口
interface StyleTag {
    value: string
    label: string
}

// 曲风标签（统一，不再区分 vocal/inst）
const genreStyleTags = computed<StyleTag[]>(() => [
    { value: 'POP', label: t('menus.musicStyles.POP') },
    { value: 'ROCK', label: t('menus.musicStyles.ROCK') },
    { value: 'RAP', label: t('menus.musicStyles.RAP') },
    { value: 'ELECTRONIC', label: t('menus.musicStyles.ELECTRONIC') },
    { value: 'RNB', label: t('menus.musicStyles.RNB') },
    { value: 'JAZZ', label: t('menus.musicStyles.JAZZ') },
    { value: 'FOLK', label: t('menus.musicStyles.FOLK') },
    { value: 'CLASSIC', label: t('menus.musicStyles.CLASSIC') },
    { value: 'WORLD', label: t('menus.musicStyles.WORLD') },
])

// 情感标签
const emotionStyleTags = computed<StyleTag[]>(() => [
    { value: 'HAPPY', label: t('menus.musicEmotions.HAPPY') },
    { value: 'RELAXED', label: t('menus.musicEmotions.RELAXED') },
    { value: 'WARM', label: t('menus.musicEmotions.WARM') },
    { value: 'ROMANTIC', label: t('menus.musicEmotions.ROMANTIC') },
    { value: 'TOUCHING', label: t('menus.musicEmotions.TOUCHING') },
    { value: 'SAD', label: t('menus.musicEmotions.SAD') },
    { value: 'LONELY', label: t('menus.musicEmotions.LONELY') },
    { value: 'DEPRESSED', label: t('menus.musicEmotions.DEPRESSED') },
    { value: 'TENSE', label: t('menus.musicEmotions.TENSE') },
    { value: 'EXCITED', label: t('menus.musicEmotions.EXCITED') },
    { value: 'EPIC', label: t('menus.musicEmotions.EPIC') },
    { value: 'MYSTERIOUS', label: t('menus.musicEmotions.MYSTERIOUS') },
])

// 乐器标签
const instrumentStyleTags = computed<StyleTag[]>(() => [
    { value: 'PIANO', label: t('menus.musicInstruments.PIANO') },
    { value: 'GUITAR', label: t('menus.musicInstruments.GUITAR') },
    { value: 'BASS', label: t('menus.musicInstruments.BASS') },
    { value: 'DRUMS', label: t('menus.musicInstruments.DRUMS') },
    { value: 'STRINGS', label: t('menus.musicInstruments.STRINGS') },
    { value: 'WIND', label: t('menus.musicInstruments.WIND') },
    { value: 'SYNTHESIZER', label: t('menus.musicInstruments.SYNTHESIZER') },
    {
        value: 'ELECTRONIC_SOUND',
        label: t('menus.musicInstruments.ELECTRONIC_SOUND'),
    },
    {
        value: 'FOLK_INSTRUMENT',
        label: t('menus.musicInstruments.FOLK_INSTRUMENT'),
    },
    {
        value: 'MIXED_ORCHESTRATION',
        label: t('menus.musicInstruments.MIXED_ORCHESTRATION'),
    },
])

// 根据当前分类获取标签
const currentTags = computed(() => {
    switch (currentCategory.value) {
        case 'genre':
            return genreStyleTags.value
        case 'emotion':
            return emotionStyleTags.value
        case 'instrument':
            return instrumentStyleTags.value
        default:
            return genreStyleTags.value
    }
})

// 获取选中的标签列表（从 Map 转为数组方便遍历）
const selectedTagsList = computed(() => {
    return Object.entries(styles.value).map(([key, value]) => ({ key, value }))
})

// 字符统计 - 基于标签（显示名称）和文本输入的总长度
const characterCount = computed(() => {
    const tagsText = Object.values(styles.value).join(', ')
    const fullText =
        tagsText + (tagsText && inputText.value ? ', ' : '') + inputText.value
    return fullText.length
})
const isNearLimit = computed(() => characterCount.value > props.maxLength * 0.8)
const isAtLimit = computed(() => characterCount.value >= props.maxLength)

// 检查标签是否被选中（根据 key 判断）
const isTagSelected = (tagKey: string) => {
    return tagKey in styles.value
}

// 切换标签选中状态
const toggleTag = (tag: StyleTag) => {
    const newStyles = { ...styles.value }
    if (tag.value in newStyles) {
        delete newStyles[tag.value]
    } else {
        newStyles[tag.value] = tag.label
    }
    styles.value = newStyles

    updatePrompt()
    emit('tag-change', newStyles)
}

// 移除指定 key 的标签
const removeTagByKey = (key: string) => {
    const newStyles = { ...styles.value }
    delete newStyles[key]
    styles.value = newStyles
    updatePrompt()
    emit('tag-change', newStyles)
}

// 更新 prompt 值（使用中文标签名称显示）
const updatePrompt = () => {
    const tagsText = Object.values(styles.value).join(', ')
    const fullText =
        tagsText + (tagsText && inputText.value ? ', ' : '') + inputText.value
    prompt.value = fullText
}

// 监听外部对 inputText 和 styles 的修改，自动更新 prompt（用于"做同款"等场景）
watch(
    [inputText, styles],
    () => {
        updatePrompt()
    },
    { deep: true }
)
</script>

<style scoped>
/* 彻底移除 textarea 所有默认样式 */
textarea {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    background: transparent !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
}

textarea:focus {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
}

textarea::placeholder {
    color: #8a8a90;
}
</style>
